import { catalogueProduct, calculateMatQuote, formatEuro, technicalSnapshot } from './smid-unilin-matten-engine.js';

const catalogues = new Map();
function loadCatalogue(url) {
  if (!catalogues.has(url)) catalogues.set(url, fetch(url, { credentials: 'omit' }).then(response => {
    if (!response.ok) throw new Error('Catalogue');
    return response.json();
  }).catch(error => { catalogues.delete(url); throw error; }));
  return catalogues.get(url);
}

class SmidMatQuote extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.controller = new AbortController();
    this.touched = new Set();
    const { signal } = this.controller;
    try {
      this.product = JSON.parse(this.querySelector('[data-um-product]').textContent);
      this.labels = JSON.parse(this.querySelector('[data-um-labels]').textContent);
    } catch { return; }
    this.variantSelect = this.querySelector('[data-um-variant]');
    this.lengthInput = this.querySelector('[data-um-length]');
    this.quantityInput = this.querySelector('[data-um-quantity]');
    this.installationInput = this.querySelector('[data-um-installation]');
    this.contact = this.querySelector('[data-um-contact]');
    this.form = this.querySelector('.um__contact-form');
    this.submitButton = this.querySelector('[data-um-submit]');
    this.requestButton = this.querySelector('[data-um-request]');
    this.addEventListener('input', event => {
      if (event.target === this.lengthInput) this.touched.add('lengthCm');
      if (event.target === this.quantityInput) this.touched.add('quantity');
      this.update();
    }, { signal });
    this.addEventListener('change', event => {
      if (event.target === this.variantSelect) {
        const url = new URL(location.href);
        url.searchParams.set('variant', this.variantSelect.value);
        history.replaceState({}, '', url);
      }
      this.update();
    }, { signal });
    this.requestButton?.addEventListener('click', () => {
      this.touched.add('lengthCm'); this.touched.add('quantity'); this.update();
      if (!this.quote?.valid) { this.lengthInput.reportValidity(); this.quantityInput.reportValidity(); return; }
      this.contact.open = true;
      this.contact.scrollIntoView({ behavior: this.motion(), block: 'start' });
      this.form?.querySelector('[name="contact[name]"]')?.focus({ preventScroll: true });
    }, { signal });
    this.form?.addEventListener('submit', event => this.submit(event), { signal });
    this.querySelector('[data-um-retry]')?.addEventListener('click', () => this.load(), { signal });
    this.querySelector('[data-um-new]')?.addEventListener('click', () => {
      const url = new URL(location.href); url.searchParams.delete('contact_posted'); url.hash = ''; location.assign(url.href);
    }, { signal });
    this.querySelectorAll('[data-um-thumbnail]').forEach(button => button.addEventListener('click', () => {
      const image = this.querySelector('[data-um-image-stage] img');
      if (!image) return;
      image.src = button.dataset.image; image.removeAttribute('srcset'); image.alt = button.dataset.alt || this.product.title;
      this.querySelectorAll('[data-um-thumbnail]').forEach(other => other.setAttribute('aria-pressed', String(other === button)));
    }, { signal }));
    this.restoreSuccess();
    this.load();
  }
  disconnectedCallback() { this.controller?.abort(); this.ready = false; }
  motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }
  text(selector, value) { this.querySelectorAll(selector).forEach(node => { node.textContent = value; }); }
  show(selector, visible) { this.querySelectorAll(selector).forEach(node => { node.hidden = !visible; }); }
  async load() {
    this.show('[data-um-load-error]', false);
    this.setAttribute('aria-busy', 'true');
    try {
      const catalogue = await loadCatalogue(this.dataset.catalogue);
      if (!this.isConnected) return;
      this.entry = catalogueProduct(catalogue, this.product);
      if (!this.entry) throw new Error('Product mapping');
      const options = this.entry.variants.map(variant => {
        const option = document.createElement('option'); option.value = variant.id; option.textContent = variant.label; return option;
      });
      const previous = this.variantSelect.value;
      this.variantSelect.replaceChildren(...options);
      const requested = this.submittedConfiguration?.variantId || new URLSearchParams(location.search).get('variant');
      const id = this.entry.variants.some(row => row.id === requested) ? requested : this.entry.variants.some(row => row.id === previous) ? previous : this.entry.variants[0].id;
      this.variantSelect.value = id;
      if (this.submittedConfiguration) {
        this.lengthInput.value = this.submittedConfiguration.lengthCm || '';
        this.quantityInput.value = this.submittedConfiguration.quantity || '1';
        this.installationInput.checked = this.submittedConfiguration.montage === true;
      }
      this.variantSelect.disabled = false;
      this.lengthInput.disabled = this.entry.unit !== 'M';
      this.show('[data-um-length-field]', this.entry.unit === 'M');
      this.show('[data-um-width-note]', this.entry.unit === 'M');
      this.text('[data-um-quantity-label]', this.labels[this.entry.unit === 'M' ? 'pieces' : this.entry.unit === 'BOX' ? 'box_quantity' : 'quantity']);
      this.text('[data-um-variant-label]', this.labels[this.entry.unit === 'M' ? 'choose_width' : 'choose_size']);
      this.update();
    } catch {
      this.entry = null;
      this.show('[data-um-load-error]', true);
      this.requestButton.disabled = true;
      if (this.submitButton) this.submitButton.disabled = true;
      this.text('[data-um-total]', this.labels.on_request);
    } finally { this.removeAttribute('aria-busy'); }
  }
  update() {
    if (!this.entry) return;
    this.quote = calculateMatQuote(this.entry, this.variantSelect.value, { lengthCm: this.lengthInput.value, quantity: this.quantityInput.value, montage: this.installationInput.checked });
    const quote = this.quote, variant = quote.variant;
    const number = value => new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 }).format(value);
    const lengthError = quote.errors.lengthCm;
    const message = lengthError === 'minimum' ? this.labels.minimum + ': ' + quote.minimum + ' cm' : lengthError === 'maximum' ? this.labels.maximum + ': ' + quote.maximum + ' cm' : lengthError ? this.labels.length_error : '';
    this.lengthInput.setCustomValidity(message);
    this.quantityInput.setCustomValidity(quote.errors.quantity ? this.labels.quantity_error : '');
    this.text('[data-um-length-error]', message);
    this.show('[data-um-length-error]', Boolean(message) && this.touched.has('lengthCm'));
    this.text('[data-um-quantity-error]', quote.errors.quantity ? this.labels.quantity_error : '');
    this.show('[data-um-quantity-error]', Boolean(quote.errors.quantity) && this.touched.has('quantity'));
    this.lengthInput.setAttribute('aria-invalid', String(Boolean(message) && this.touched.has('lengthCm')));
    this.quantityInput.setAttribute('aria-invalid', String(Boolean(quote.errors.quantity) && this.touched.has('quantity')));
    this.text('[data-um-minimum]', (quote.minimum === null ? this.labels.minimum_confirm : this.labels.minimum + ': ' + quote.minimum + ' cm') + (quote.maximum === null ? '' : ' · ' + this.labels.maximum + ': ' + quote.maximum + ' cm'));
    const perUnit = this.labels[quote.unit === 'M' ? 'per_m' : quote.unit === 'BOX' ? 'per_box' : 'per_item'];
    this.text('[data-um-unit-price]', quote.unitPriceCents === null ? this.labels.on_request : formatEuro(quote.unitPriceCents) + ' ' + perUnit);
    this.text('[data-um-total]', quote.valid ? quote.totalCents === null ? this.labels.on_request : formatEuro(quote.totalCents) : this.labels.fill_dimensions);
    this.text('[data-um-total-note]', quote.totalCents === null && quote.valid ? this.labels.unknown_price_note : this.labels.incl_vat);
    this.text('[data-um-summary-variant]', variant?.label || '—');
    this.text('[data-um-summary-length]', quote.lengthCm === null ? '—' : quote.lengthCm + ' cm');
    this.show('[data-um-summary-length-row]', quote.unit === 'M');
    const quantityKey = quote.unit === 'BOX' ? quote.quantity === 1 ? 'box_singular' : 'boxes' : quote.quantity === 1 ? 'piece_singular' : 'piece_short';
    this.text('[data-um-summary-quantity]', quote.quantity === null ? '—' : quote.quantity + ' ' + this.labels[quantityKey]);
    this.text('[data-um-summary-area]', quote.areaM2 === null ? '—' : number(quote.areaM2) + ' m²');
    this.show('[data-um-summary-area-row]', quote.areaM2 !== null);
    this.text('[data-um-summary-installation]', quote.montage ? this.labels.installation_requested : this.labels.no_installation);
    this.text('[data-um-checked]', variant?.priceCheckedOn ? this.labels.checked_on + ' ' + variant.priceCheckedOn : '');
    this.show('[data-um-checked]', Boolean(variant?.priceCheckedOn));
    this.requestButton.disabled = !quote.valid;
    if (this.submitButton) this.submitButton.disabled = !quote.valid;
    this.snapshot = technicalSnapshot(this.product, quote, this.labels, new URL(this.product.url, location.origin).href);
    const body = this.form?.querySelector('[data-um-body]');
    if (body) {
      const message = this.form.querySelector('[data-um-message]').value.trim();
      body.value = this.snapshot + (message ? '\n\n' + this.labels.message + ':\n' + message : '');
    }
  }
  submit(event) {
    this.touched.add('lengthCm'); this.touched.add('quantity'); this.update();
    if (!this.quote?.valid || !this.form.reportValidity()) {
      event.preventDefault();
      if (!this.quote?.valid) { this.lengthInput.reportValidity(); this.quantityInput.reportValidity(); }
      return;
    }
    const message = this.form.querySelector('[data-um-message]').value.trim();
    this.form.querySelector('[data-um-body]').value = this.snapshot + (message ? '\n\n' + this.labels.message + ':\n' + message : '');
    try { sessionStorage.setItem('smid:matten:submitted:' + this.product.handle, JSON.stringify({ createdAt: Date.now(), technical: this.snapshot, configuration: { variantId: this.variantSelect.value, lengthCm: this.quote.lengthCm, quantity: this.quote.quantity, montage: this.quote.montage } })); } catch {}
  }
  restoreSuccess() {
    const success = this.querySelector('[data-um-success]');
    if (!success) return;
    this.contact.open = true;
    this.requestButton.hidden = true;
    try {
      const saved = JSON.parse(sessionStorage.getItem('smid:matten:submitted:' + this.product.handle));
      if (saved && typeof saved.technical === 'string' && Date.now() - saved.createdAt < 86400000 && Date.now() >= saved.createdAt) {
        this.text('[data-um-submitted-summary]', saved.technical);
        this.show('[data-um-submitted-summary]', true);
        this.submittedConfiguration = saved.configuration;
      }
    } catch {}
    requestAnimationFrame(() => { success.focus({ preventScroll: true }); success.scrollIntoView({ block: 'center' }); });
  }
}

if (!customElements.get('smid-mat-quote')) customElements.define('smid-mat-quote', SmidMatQuote);
